// Edición múltiple: qué campos de la ficha se ofrecen y con qué editor. Lógica pura
// (con test) para que el modal no pueda dejar cambios que el API no guarda:
// - Los calculados no se ofrecen: se recalculan solos al guardar y el cambio se perdía.
// - Código y nombre de tienda identifican cada obra: no tiene sentido ponérselos a varias.
// - PM/PM2/PM3/PMI se eligen entre los usuarios habilitados y se guardan por su código
//   (codPm...): con el nombre como texto el API no guardaba nada.
// - La ubicación depende del país: solo se ofrecen las del país común de la selección.

export const BULK_IDENTITY_FIELDS = new Set(["codigoTienda", "tienda"]);

const editorOf = (config) => {
    if (config.multiline) return "textarea";
    if (config.type === "date") return "date";
    if (config.type === "number") return "number";
    return "text";
};

// Vacío se queda en "" (el guardado lo envía como null): así una obra que ya no tenía
// responsable no genera un cambio «Sin dato -> Sin dato».
const toResponsableCode = (value) => (value === "" || value == null ? "" : Number(value));

// groups: excelFieldGroups. isCalculated(field): además de `calculated`, los costes que
// deriva SQL. Devuelve [{ id, label, fields: [{ id, label, editor, options, maxLength, parse }] }].
export function buildBulkFieldGroups(groups, { catalogs = {}, responsablesByRol = {}, isCalculated = () => false } = {}) {
    return groups.map((group) => ({
        id: group.id,
        label: group.label,
        fields: group.fields
            .filter((config) => !config.calculated && !isCalculated(config.field) && !BULK_IDENTITY_FIELDS.has(config.field))
            .map((config) => {
                if (config.usuario) {
                    return {
                        id: config.usuario,
                        label: config.label,
                        editor: "select",
                        options: (responsablesByRol[config.rol] || []).map((usuario) => ({ value: String(usuario.codUsuario), label: usuario.nombre })),
                        parse: toResponsableCode,
                    };
                }
                if (config.options) {
                    // Lista fija (Sol. Zonif.: Sí/No).
                    return { id: config.field, label: config.label, editor: "select", options: config.options.map((value) => ({ value, label: value })) };
                }
                if (config.catalog) {
                    return {
                        id: config.field,
                        label: config.label,
                        editor: "select",
                        catalog: config.catalog,
                        // La ubicación se resuelve con bulkCityChoice (depende del país).
                        options: config.catalog === "ciudad"
                            ? null
                            : (catalogs[config.catalog] || []).filter((entry) => entry.active !== false).map((entry) => ({ value: entry.value, label: entry.value })),
                    };
                }
                return { id: config.field, label: config.label, editor: editorOf(config), maxLength: config.maxLength || null };
            }),
    })).filter((group) => group.fields.length);
}

// Ubicaciones que se pueden poner a toda la selección. `paisOverride` es el país marcado
// en el propio modal (undefined si no se cambia): manda sobre el de las obras.
// Mismo criterio exacto que el guardado (entry.parent === pais).
export function bulkCityChoice(ciudadCatalog, obras, paisOverride) {
    const paises = paisOverride !== undefined
        ? [paisOverride || ""]
        : [...new Set(obras.map((obra) => obra.pais || ""))];
    if (paises.length > 1) {
        return { options: [], reason: "Las obras seleccionadas son de países distintos: marca también un país o cámbialas por separado." };
    }
    if (!paises[0]) return { options: [], reason: "Elige antes un país." };
    return {
        options: (ciudadCatalog || [])
            .filter((entry) => entry.active !== false && entry.parent === paises[0])
            .map((entry) => ({ value: entry.value, label: entry.value })),
        reason: "",
    };
}

// Cambios que deja el modal: solo los campos marcados, con su valor ya convertido
// (códigos de usuario como número; vacío = quitar el dato).
export function bulkChanges(fields, values, touched) {
    const changes = {};
    fields.forEach((field) => {
        if (!touched[field.id]) return;
        const value = values[field.id] ?? "";
        changes[field.id] = field.parse ? field.parse(value) : value;
    });
    return changes;
}
